import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle2,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/client';
import { dateTime, errorMessage, shortDate } from '../ui/format';
import { LAYER_LABEL, RUN_COLOR, duration } from '../readiness/format';

const useStyles = makeStyles({
  head: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalM },
  spacer: { flex: 1 },
  intro: { maxWidth: '78ch', marginBlock: tokens.spacingVerticalS },
  tabs: { marginBlock: tokens.spacingVerticalM },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  wrap: { overflowX: 'auto' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, whiteSpace: 'nowrap' },
  sub: { display: 'block', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  nowrap: { whiteSpace: 'nowrap' },
  num: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
});

type TabId = 'templates' | 'personas' | 'data' | 'runs';

/** /testing: the readiness test library (build update 2.4, section 9). */
export function TestingPage() {
  const styles = useStyles();
  const api = useApi();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('templates');
  const lib = useQuery({ queryKey: ['testing-library'], queryFn: () => api.getTestingLibrary() });

  if (lib.isPending) return <Spinner label="Loading the test library…" />;
  if (lib.isError)
    return (
      <MessageBar intent="error">
        <MessageBarBody>{errorMessage(lib.error)}</MessageBarBody>
      </MessageBar>
    );
  const l = lib.data;

  return (
    <div>
      <div className={styles.head}>
        <Title2 as="h1">Readiness testing</Title2>
        <span className={styles.spacer} />
        <Button appearance="primary" onClick={() => navigate('/testing/campaigns')}>
          Upgrade campaigns
        </Button>
      </div>
      <Body1 as="p" className={styles.intro}>
        One library of Playwright test templates. Each venue&apos;s plan is generated from its people, roles
        and approval chain; the same library is the regression suite when Microsoft updates D365 TEST.
      </Body1>
      <TabList className={styles.tabs} selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as TabId)}>
        <Tab value="templates">Templates ({l.templates.length})</Tab>
        <Tab value="personas">Personas ({l.personas.length})</Tab>
        <Tab value="data">Data sets</Tab>
        <Tab value="runs">Runs ({l.runs.length})</Tab>
      </TabList>

      {tab === 'templates' && (
        <Card className={styles.card}>
          <div className={styles.wrap}>
            <Table size="small" aria-label="Test templates">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell style={{ width: '12%' }}>Template</TableHeaderCell>
                  <TableHeaderCell style={{ width: '9%' }}>Layer</TableHeaderCell>
                  <TableHeaderCell style={{ width: '9%' }}>Persona</TableHeaderCell>
                  <TableHeaderCell style={{ width: '21%' }}>What it does</TableHeaderCell>
                  <TableHeaderCell style={{ width: '17%' }}>Passes when</TableHeaderCell>
                  <TableHeaderCell style={{ width: '6%' }}>Cases</TableHeaderCell>
                  <TableHeaderCell style={{ width: '8%' }}>Pass rate</TableHeaderCell>
                  <TableHeaderCell style={{ width: '9%' }}>Last run</TableHeaderCell>
                  <TableHeaderCell style={{ width: '9%' }}>To review</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {l.templates.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <b className={styles.mono}>{t.id}</b>
                      <span className={styles.sub}>
                        {t.built ? 'built (R1)' : 'planned'} · {t.owner}
                      </span>
                    </TableCell>
                    <TableCell className={styles.nowrap}>
                      {t.layer} {LAYER_LABEL[t.layer]}
                    </TableCell>
                    <TableCell>{t.persona}</TableCell>
                    <TableCell>
                      {t.what}
                      {t.module && <span className={styles.sub}>Only with the {t.module} module</span>}
                    </TableCell>
                    <TableCell>{t.passesWhen}</TableCell>
                    <TableCell className={styles.num}>{t.cases}</TableCell>
                    <TableCell className={styles.num}>
                      {t.passRate === null ? '—' : `${Math.round(t.passRate * 100)}%`}
                      {t.runs > 0 && <span className={styles.sub}>{t.runs} runs</span>}
                    </TableCell>
                    <TableCell className={styles.nowrap}>{shortDate(t.lastRunAt)}</TableCell>
                    <TableCell>
                      {t.healerProposals > 0 ? (
                        <Badge appearance="tint" color="warning">
                          {t.healerProposals} selector fix{t.healerProposals > 1 ? 'es' : ''}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Caption1>
            Selector fixes come from failures classed as a UI change; a person reviews each one before it
            merges, because a fix can also hide a real defect.
          </Caption1>
        </Card>
      )}

      {tab === 'personas' && (
        <Card className={styles.card}>
          <Body1>
            One synthetic account per venue seat, in D365 TEST only. Each gets exactly the role bundle of the
            person in that seat, scoped to the venue company, and is seated in that position so approvals
            route to an account the tests can sign in as. Real staff are seated in PROD only.
          </Body1>
          <div className={styles.wrap}>
            <Table size="small" aria-label="Test personas">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Persona</TableHeaderCell>
                  <TableHeaderCell>Test account</TableHeaderCell>
                  <TableHeaderCell>TEST seat</TableHeaderCell>
                  <TableHeaderCell>Role bundle</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {l.personas.map((p) => (
                  <TableRow key={p.key}>
                    <TableCell>
                      <b>{p.label}</b>
                      <span className={styles.sub}>key {p.key}</span>
                    </TableCell>
                    <TableCell className={styles.mono}>{p.account}</TableCell>
                    <TableCell className={styles.nowrap}>
                      &lt;company&gt;-{p.seat}
                      <span className={styles.sub}>job {p.jobId}</span>
                    </TableCell>
                    <TableCell>
                      {p.bundleNames.join(', ') || '—'}
                      <span className={styles.sub}>{p.bundle.join(', ')}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Caption1>
            Regional finance is shared across venues, so no persona approves as it: the over-limit case only
            checks the work item reaches REG-FIN-01.
          </Caption1>
        </Card>
      )}

      {tab === 'data' && (
        <div className={styles.stack}>
          <Card className={styles.card}>
            <Subtitle2 as="h2">Data sets per archetype</Subtitle2>
            <Table size="small" aria-label="Readiness data sets">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Archetype</TableHeaderCell>
                  <TableHeaderCell>Version</TableHeaderCell>
                  <TableHeaderCell>Test vendor</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {l.dataSets.map((d) => (
                  <TableRow key={d.archetype}>
                    <TableCell>{d.archetype}</TableCell>
                    <TableCell className={styles.mono}>{d.version}</TableCell>
                    <TableCell className={styles.mono}>{d.vendor}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className={styles.card}>
            <Subtitle2 as="h2">What every run needs</Subtitle2>
            <Table size="small" aria-label="Setup data">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Need</TableHeaderCell>
                  <TableHeaderCell>Source</TableHeaderCell>
                  <TableHeaderCell>Preflight check</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {l.setupData.map((s) => (
                  <TableRow key={s.key}>
                    <TableCell>{s.need}</TableCell>
                    <TableCell>{s.source}</TableCell>
                    <TableCell>{s.check}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Caption1>
              Every document a test creates carries the run ID; nothing is deleted. D365 TEST is refreshed
              from PROD on the normal schedule.
            </Caption1>
          </Card>
        </div>
      )}

      {tab === 'runs' && (
        <Card className={styles.card}>
          <div className={styles.wrap}>
            <Table size="small" aria-label="Readiness runs across venues">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Started</TableHeaderCell>
                  <TableHeaderCell>Venue</TableHeaderCell>
                  <TableHeaderCell>Run</TableHeaderCell>
                  <TableHeaderCell>D365</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Passed</TableHeaderCell>
                  <TableHeaderCell>Failed</TableHeaderCell>
                  <TableHeaderCell>Test time</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {l.runs.map((r) => (
                  <TableRow
                    key={r.id}
                    onClick={r.requestId ? () => navigate(`/requests/${r.requestId}`) : undefined}
                    style={r.requestId ? { cursor: 'pointer' } : undefined}
                  >
                    <TableCell className={styles.nowrap}>
                      {r.startedAt ? dateTime(r.startedAt) : '—'}
                    </TableCell>
                    <TableCell>
                      <b className={styles.mono}>{r.company}</b>
                      {r.venueName && <span className={styles.sub}>{r.venueName}</span>}
                    </TableCell>
                    <TableCell>{r.label ?? `Readiness · ${r.environment}`}</TableCell>
                    <TableCell>{r.d365Version ?? '—'}</TableCell>
                    <TableCell>
                      <Badge appearance="tint" color={RUN_COLOR[r.status]}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className={styles.num}>
                      {r.passed}/{r.total}
                    </TableCell>
                    <TableCell className={styles.num}>{r.failed + r.waived}</TableCell>
                    <TableCell className={styles.num}>{duration(r.durationMs)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
